// Windowed, zoom-adaptive reduction of a dense peaks array. The BE waveform
// (sourceWaveform.js) is ~50 peaks per second — a 90-minute source is ~270k
// points, far more than any zoom can show. Drawing them all in one SVG path is
// what froze the timeline on weak machines, so instead the visible window is
// reduced to about one envelope point per pixel: the most detail a pixel can
// display, and a bounded cost at every zoom for any duration.
//
// Reduction is per-bucket max — never a stride. A stride skips points and can
// drop a transient entirely (aliasing); the bucket max keeps every loud
// moment, which is exactly the classic waveform envelope.

/**
 * Plan which slice of `peaks` the view needs and how coarsely to reduce it.
 *
 * Buckets are aligned to absolute peak indexes (bucket k always covers peaks
 * [k*bucketSize, (k+1)*bucketSize)), so scrolling never changes a bucket's
 * contents — only which buckets are visible — and the reduced bands can be
 * reused between frames. `bucketSize` is the peaks per bucket at this zoom;
 * 1 means the raw peaks are drawn exactly.
 *
 * `leftPx`/`widthPx` place the window in the clip's own coordinate space
 * (second s sits at s*pxPerSec from the clip's start).
 *
 * @returns {{
 *   bucketSize: number, firstBucket: number, bucketCount: number,
 *   leftPx: number, widthPx: number,
 * } | null} null when there is nothing to draw.
 */
export const planWaveformWindow = ({
  peakCount,
  durationSec,
  fromSec,
  toSec,
  pxPerSec,
  marginRatio = 0.25,
}) => {
  if (!peakCount || peakCount < 2) return null;
  if (!(durationSec > 0) || !(pxPerSec > 0) || !(toSec > fromSec)) return null;
  const peaksPerSecond = peakCount / durationSec;
  const bucketSize = Math.max(1, Math.ceil(peaksPerSecond / pxPerSec));
  // A little slack each side so a small scroll is already covered before the
  // next plan lands.
  const margin = (toSec - fromSec) * marginRatio;
  const fromPeak = Math.max(0, Math.floor((fromSec - margin) * peaksPerSecond));
  const toPeak = Math.min(
    peakCount,
    Math.ceil((toSec + margin) * peaksPerSecond),
  );
  if (toPeak <= fromPeak) return null;
  const firstBucket = Math.floor(fromPeak / bucketSize);
  const bucketCount = Math.ceil(toPeak / bucketSize) - firstBucket;
  if (bucketCount < 2) return null;
  const pxPerPeak = pxPerSec / peaksPerSecond;
  return {
    bucketSize,
    firstBucket,
    bucketCount,
    leftPx: firstBucket * bucketSize * pxPerPeak,
    widthPx: bucketCount * bucketSize * pxPerPeak,
  };
};

// The loudest peak in peaks[from..to). A plain loop on purpose: this is the
// one line that runs for EVERY peak in the window (~270k on a 90-minute Fit),
// and the pretty alternatives measure far slower there — subarray().reduce()
// ~9x, Math.max(...spread) ~19x (and the spread overflows the stack on big
// buckets). Everything around it can afford to be declarative; this can't.
const loudestIn = (peaks, from, to) => {
  let loudest = 0;
  for (let i = from; i < to; i += 1) {
    if (peaks[i] > loudest) loudest = peaks[i];
  }
  return loudest;
};

/**
 * Reduce the planned window of `peaks` to its max envelope — bucket k of the
 * result is the loudest of the `bucketSize` peaks it stands for, 0..1 like the
 * input. At bucketSize 1 this is the raw peaks slice: the exact shape, no
 * reduction.
 *
 * @returns {Float32Array}
 */
export const reduceWaveformWindow = (
  peaks,
  { firstBucket, bucketCount, bucketSize },
) =>
  Float32Array.from({ length: bucketCount }, (_, bucket) => {
    const from = (firstBucket + bucket) * bucketSize;
    // Only the window's last bucket can run past the end of the peaks.
    const to = Math.min(peaks.length, from + bucketSize);
    return loudestIn(peaks, from, to);
  });
